// HYK-715: Add Instagram handle in onboarding as mandatory - "Add social links" screen
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon/Icon";
import { USER_ROLES } from "../../constants/globals";
import { SIGNUP_CREATOR_SESSION_KEY } from "../../constants/signupFlow";
import { fetchUpdateProfile } from "../../Reducers/actions/userActions";
import { updateUser } from "../../Reducers/actions/authActions";
import { AuthFormStackSkeleton } from "../../components/Skeletons/BatchScreenSkeletons";
import {
  formatSocialUrl,
  validateSocialUrl,
  extractUsernameFromUrl,
} from "../../../helpers/socialLinks";
import "./AuthCommLink.scss";
import "./AddSocialLinks.scss";

const SOCIALS = [
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "Your Instagram profile",
    icon: "/social-icons/instagram.svg",
  },
  {
    key: "youtube",
    label: "Youtube",
    placeholder: "Your Youtube channel",
    icon: "/social-icons/youtube.svg",
  },
  {
    key: "twitter",
    label: "X",
    placeholder: "Your X profile",
    icon: "/social-icons/x.svg",
  },
  {
    key: "tiktok",
    label: "TikTok",
    placeholder: "Your TikTok profile",
    icon: "/social-icons/tiktok.svg",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    placeholder: "Your LinkedIn profile",
    icon: "/social-icons/linkedin.svg",
  },
];

const INSTAGRAM_KEY = "instagram";
const INSTAGRAM_INDEX = 0;

function AddSocialLinks() {
  const user = useSelector((state) => state.auth?.user || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showMoreLinks, setShowMoreLinks] = useState(false);
  const [rawInputs, setRawInputs] = useState({
    instagram: "",
    youtube: "",
    twitter: "",
    tiktok: "",
    linkedin: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /** Creators (and creator onboarding) must add Instagram; customers may skip this screen. */
  const socialLinksRequired = useMemo(() => {
    try {
      if (sessionStorage.getItem(SIGNUP_CREATOR_SESSION_KEY) === "1") return true;
    } catch {
      // ignore
    }
    return (
      user?.role === USER_ROLES.creator || user?.role === USER_ROLES.admin
    );
  }, [user?.role]);

  const visibleSocials = showMoreLinks
    ? SOCIALS
    : SOCIALS.slice(INSTAGRAM_INDEX, INSTAGRAM_INDEX + 1);

  useEffect(() => {
    if (user?.user_id) {
      setRawInputs({
        instagram: user.instagram
          ? extractUsernameFromUrl("instagram", user.instagram)
          : "",
        youtube: user.youtube
          ? extractUsernameFromUrl("youtube", user.youtube)
          : "",
        twitter: user.twitter
          ? extractUsernameFromUrl("twitter", user.twitter)
          : "",
        tiktok: user.tiktok
          ? extractUsernameFromUrl("tiktok", user.tiktok)
          : "",
        linkedin: user.linkedin
          ? extractUsernameFromUrl("linkedin", user.linkedin)
          : "",
      });
    }
  }, [user?.user_id]);

  const handleChange = (key, value) => {
    setRawInputs((prev) => ({ ...prev, [key]: value }));
    const trimmed = (value || "").trim();
    const isValid = trimmed === "" || validateSocialUrl(key, value);
    setValidationErrors((prev) => ({ ...prev, [key]: !isValid }));
    setError(null);
  };

  const handleSkip = () => {
    if (socialLinksRequired) return;
    navigate("/complete-profile");
  };

  const handleContinue = () => {
    const instagramValue = rawInputs[INSTAGRAM_KEY]?.trim() || "";

    if (socialLinksRequired) {
      if (!instagramValue) {
        setError("Instagram is required");
        setValidationErrors((prev) => ({ ...prev, [INSTAGRAM_KEY]: true }));
        return;
      }

      if (!validateSocialUrl(INSTAGRAM_KEY, instagramValue)) {
        setError("Please enter a valid Instagram username");
        setValidationErrors((prev) => ({ ...prev, [INSTAGRAM_KEY]: true }));
        return;
      }
    } else {
      if (
        instagramValue &&
        !validateSocialUrl(INSTAGRAM_KEY, instagramValue)
      ) {
        setError("Please enter a valid Instagram username");
        setValidationErrors((prev) => ({ ...prev, [INSTAGRAM_KEY]: true }));
        return;
      }
    }

    const hasInvalidOther = visibleSocials.some((s) => {
      const val = rawInputs[s.key]?.trim();
      return val && !validateSocialUrl(s.key, val);
    });

    if (hasInvalidOther) {
      setError("Please fix invalid usernames");
      return;
    }

    if (!socialLinksRequired) {
      const hasAnyInput = SOCIALS.some(({ key }) =>
        String(rawInputs[key] || "").trim(),
      );
      if (!hasAnyInput) {
        navigate("/complete-profile");
        return;
      }
    }

    setLoading(true);
    setError(null);

    const formattedLinks = {};
    SOCIALS.forEach(({ key }) => {
      const val = rawInputs[key]?.trim();
      formattedLinks[key] = val ? formatSocialUrl(key, val) : "";
    });

    const payload = {
      user_id: user.user_id,
      instagram: formattedLinks.instagram,
      youtube: formattedLinks.youtube,
      twitter: formattedLinks.twitter,
      tiktok: formattedLinks.tiktok,
      linkedin: formattedLinks.linkedin,
    };

    fetchUpdateProfile(payload, user.user_id)
      .then(({ success, message }) => {
        if (success) {
          dispatch(updateUser({ ...user, ...formattedLinks }));
          navigate("/complete-profile");
        } else {
          setError(message || "Failed to save. Please try again.");
        }
      })
      .catch(() => {
        setError("An error occurred. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!user?.user_id) {
    return (
      <div className="d-flex f-column aic jcc p-16" style={{ minHeight: '50dvh' }} role="status" aria-label="Loading">
        <AuthFormStackSkeleton fields={3} />
      </div>
    );
  }

  return (
    <div
      className="d-flex f-column aic bg-red p-fixed"
      style={{
        height: "100%",
        width: "100dvw",
        top: 0,
        left: 0,
        overflow: "scroll",
      }}
    >
      <div className="complete-profile-container pt-40 mt-40 auth-bg">
        <span
          className="p-absolute back-icon"
          onClick={handleBack}
          onKeyDown={(e) => e.key === "Enter" && handleBack()}
          role="button"
          tabIndex={0}
          aria-label="Go back"
        >
          <Icon alias="chevron-left" width={24} color="#000" className="ml-16" />
        </span>
        <p className="fs-sm2 fw-bold mb-32 mt-12 ta-center">Add social links</p>
        <p className="add-social-links-subtitle fg-hyk-medgrey ta-center mt-0 mb-32">
          {socialLinksRequired
            ? "Help people discover your HYK profile. Instagram is required for creators."
            : "Add links if you like — you can skip this step and finish your profile next."}
        </p>

        <div className="d-flex f-column w-full p-16 body-complete add-social-links-fields">
          {visibleSocials.map(({ key, placeholder, icon }) => (
            <div key={key} className="add-social-links-field">
              <img
                src={icon}
                alt={key}
                width={24}
                height={24}
                className="add-social-links-icon"
              />
              <input
                type="text"
                value={rawInputs[key] || ""}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={placeholder}
                className={`input-main add-social-links-input ${validationErrors[key] ? "invalid" : ""}`}
                onFocus={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                aria-label={placeholder}
                aria-invalid={!!validationErrors[key]}
              />
            </div>
          ))}

          {!showMoreLinks && (
            <button
              type="button"
              className="add-social-links-more"
              onClick={() => setShowMoreLinks(true)}
              aria-label="Add more social links"
            >
              MORE LINKS
            </button>
          )}
        </div>

        {error && (
          <p className="add-social-links-error fg-red fs-xs ta-center px-16">
            {error}
          </p>
        )}

        <div className="p-fixed bg-white auth-footer pb-24 mt-24" style={{ bottom: 0, left: 0 }}>
          {!socialLinksRequired && (
            <button
              type="button"
              className="btn red-outline-btn btn-full-v d-flex aic jcc p-relative mb-12"
              onClick={handleSkip}
              disabled={loading}
              aria-label="Skip adding social links"
            >
              Skip for now
            </button>
          )}
          <button
            type="button"
            className="btn red-btn btn-full-v d-flex aic jcc p-relative"
            onClick={handleContinue}
            disabled={loading}
            aria-label="Continue"
          >
            {loading ? "SAVING..." : "CONTINUE"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddSocialLinks;
